import { useState } from "react";
import Button from "./foundational/Button";
import PlusBadge from "./PlusBadge";
import ComparaPlanes from "./ComparaPlanes";
import ContratarMembresia from "./ContratarMembresia";
import { enTransicionEstudiante, caraMipaePlus, caraContratable, ofertaPara, ctaPaywall, cuotaPara, accesoPlusAbierto, topeUsoJusto } from "../lib/plan";
import { diasTrialPara } from "../lib/planGating";
import { isNative } from "../lib/platform";

/**
 * PremiumPaywall — hoja inferior (MiPAE extendido) que aparece al alcanzar la cuota
 * mensual de IA o al tocar una función exclusiva.
 *
 * Un solo IAP, presentación por rol: el cuerpo (gancho + beneficios) lo da la
 * "cara" de MiPAE extendido del rol del usuario (caraMipaePlus), mientras el disparador
 * (cuota agotada / función exclusiva) solo aporta una línea de contexto. Misma
 * membresía, propuesta de valor distinta por capa.
 *
 * Dos variantes según plataforma (políticas de las tiendas):
 *  - WEB (mipae.cl): flujo de pago completo vía Flow (selección de plan + CTA).
 *  - APP NATIVA (Capacitor iOS/Android): modelo "consumption-only" exigido por
 *    Apple/Google fuera de EE.UU. — NO se muestran precios, NO hay botón de
 *    compra ni enlace al checkout. Solo se informa que la membresía se
 *    administra desde la cuenta en mipae.cl (gestión de cuenta, no venta).
 *
 * Props:
 *  - feature: 'pae' | 'revisa' | 'detallado' | 'estudio' | 'bienvenida' |
 *    'registro' | 'entrega' | 'evolucion' | 'perfil' | 'pdf_benito' — disparador (contexto).
 *  - profile: perfil del usuario (resuelve la cara por rol).
 *  - onClose: cerrar.
 *  - onCompletarPerfil: ir a completar el perfil (cara sin rol).
 *  - trialDisponible: si puede iniciar la prueba de su experiencia.
 *  - perfilReal: el perfil de la casa. Si difiere del que pinta la hoja (la
 *    persona está de visita en otro mundo), la hoja no vende ahí: el
 *    servidor cobra según el rol REAL y rechazaría lo que el mundo visitado
 *    ofrece (a un TENS en MiPAE Enfermero, Enfermero Plus). Lleva a Planes,
 *    que ofrece lo que la persona sí puede contratar.
 *  - fueraDeCasa: { casa, onVolver } cuando la persona tiene MiPAE extendido
 *    en su casa y topó el límite de visita en otro mundo (tres mundos,
 *    docs/plan-tres-mundos.md). No se le vende lo que ya tiene ni se le gasta
 *    la prueba: se le dice dónde no tiene límite y se le ofrece volver.
 */
export default function PremiumPaywall({
  feature = "pae",
  profile = null,
  onClose,
  onCompletarPerfil,
  onTrialIniciado,
  trialDisponible = false,
  onMetric,
  onVerPlanes,
  fueraDeCasa = null,
  perfilReal = null,
}) {
  const [trialLoading] = useState(false);
  const [error] = useState(null);

  // La prueba comienza solo después del registro verificado en Flow.
  const handleTrial = () => {
    if (!isNative) window.location.assign('/?planes=1');
  };

  // Cara de MiPAE extendido del rol → gancho, bajada y beneficios.
  const cara = caraMipaePlus(profile);
  // Se vende la membresía de la experiencia (y Full); la cara sin rol no vende,
  // y de visita en otro mundo tampoco se vende aquí (ver `perfilReal`).
  const deVisita = !!perfilReal && perfilReal.rol !== profile?.rol;
  const contratable = caraContratable(cara) && !deVisita;
  const ofreceEnPlanes = deVisita && !!onVerPlanes && ofertaPara(perfilReal).length > 0;
  const titulo = cara.gancho;
  const bajada = cara.bajada;
  const BENEFICIOS = cara.beneficios;

  // Línea de contexto según el disparador (qué tocó el usuario). Honesta sobre
  // el motivo, sin robarle el protagonismo al gancho del rol.
  // Cuota de PAE (MiPAE extendido, Fase 1, 2026-09-14): el Detallado ya no
  // es candado sino cuota, y el copy dice cuántos trae el plan gratis y
  // cuándo se renuevan. Es la cuota, no un contador "te quedan N" (anti-culpa).
  const cuota = cuotaPara(profile);
  const contexto = profile?.rol === 'estudiante' && !enTransicionEstudiante(profile) && ['pae', 'detallado', 'pauta'].includes(feature)
    ? 'Este mes ya usaste tu PAE gratuito. El cupo es compartido entre Rápido, Detallado y con tu pauta, y se renueva el 1° del mes.'
    :
    feature === "detallado"
      ? `Este mes ya usaste tus ${cuota.detallados} PAE Detallado del plan gratis. Se renuevan el 1° del mes.`
      : feature === "revisa"
        ? "Llegaste a tu límite mensual de Revisa."
        : feature === "pae"
          ? `Este mes ya usaste tus ${cuota.paes} PAE del plan gratis. Se renuevan el 1° del mes.`
        : feature === "pauta"
          ? "Este mes ya usaste tu PAE con tu pauta del plan gratis. Se renueva el 1° del mes."
          // Las puertas de módulo de junio (estudio, registro, entrega,
          // evolucion) se abrieron a todos (D1): sus disparadores quedan por
          // compatibilidad, pero ya no hay copy que diga que son de pago.
          : feature === "pdf_benito"
            ? "Descargar la respuesta de Benito en PDF es parte de Estudiante Plus, Enfermero Plus y Full. Copiarla sigue gratis."
          : feature === "bienvenida" && !isNative && trialDisponible
            ? `Te damos la bienvenida. Puedes probar gratis por ${diasTrialPara(perfilReal || profile)} días lo que suma una membresía.`
            : null;

  const ctaLabel = ctaPaywall(trialDisponible, perfilReal || profile);

  // Límite de uso con el acceso abierto (2026-09-26). Mientras el lanzamiento
  // siga abierto, los topes de uso justo (PAE y Detallado; Benito tiene su
  // propia burbuja) rigen pero NO hay venta: la hoja de venta de junio decía
  // que el Detallado y MiPAE en turno eran de pago y, en Android, ofrecía
  // suscribir en mipae.cl, las dos cosas falsas o prohibidas hoy (Términos §6,
  // D7 del plan de la extendida). Acá solo se informa el límite, que lo creado
  // sigue ahí y, SOLO en web, que la suscripción lo quita. Sin trial, sin
  // precios, sin badge: la misma regla de Planes con el acceso abierto.
  if (fueraDeCasa) {
    return (
      <HojaLimiteAbierto
        feature={feature}
        contexto={contexto}
        onClose={onClose}
        onVerPlanes={null}
        vuelta={fueraDeCasa}
      />
    );
  }

  // MiPAE TENS es gratuito y no tiene membresía (Sebastián, 17-oct): a un
  // TENS, o dentro de MiPAE TENS, no se ofrece prueba ni membresía. Se dice el
  // límite y que lo creado sigue ahí, nada más; los límites no cambian. Va
  // DESPUÉS de fueraDeCasa: quien tiene membresía en su casa sigue viendo
  // dónde no tiene el límite. El contexto del PDF nombra las membresías, así
  // que aquí se dice solo lo que sigue gratis.
  if (profile?.rol === "tens" || perfilReal?.rol === "tens") {
    return (
      <HojaLimiteAbierto
        feature={feature}
        contexto={feature === "pdf_benito" ? "Copiar la respuesta sigue gratis." : contexto}
        onClose={onClose}
        onVerPlanes={null}
      />
    );
  }

  if (profile?.rol !== 'estudiante' && accesoPlusAbierto() && topeUsoJusto(feature) && (feature === "pae" || feature === "detallado")) {
    return (
      <HojaLimiteAbierto
        feature={feature}
        contexto={contexto}
        onClose={onClose}
        onVerPlanes={isNative ? null : onVerPlanes}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="premium-title"
    >
      <div
        className="bg-paper w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 shadow-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1 bg-edge rounded-full mx-auto mb-4 sm:hidden" />

        {isNative ? (
          /* Nativo: solo explica el tope y ofrece la prueba gratuita.
             Sin beneficios, comparación, precio ni promoción de compra. */
          <div className="mb-5">
            <h2 id="premium-title" className="font-serif text-xl font-bold text-ink mb-2">
              {contexto || "Llegaste al límite del plan gratis."}
            </h2>
            {!isNative && trialDisponible && (
              <p className="text-sm text-ink-subtle font-sans leading-relaxed">
                Puedes seguir ahora con la prueba gratuita de {diasTrialPara(perfilReal || profile)} días.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="mb-2">
              <PlusBadge size="sm" nombre={cara.nombre} />
            </div>
            {contexto && (
              <p className="text-12px text-ink-muted font-sans mb-1.5">{contexto}</p>
            )}
            <h2 id="premium-title" className="font-serif text-xl font-bold text-ink mb-2">
              {titulo}
            </h2>
            <p className="text-sm text-ink-subtle font-sans leading-relaxed mb-5">
              {bajada}
            </p>

            <ul className="flex flex-col gap-2.5 mb-5">
              {BENEFICIOS.map((beneficio) => (
                <li key={beneficio} className="flex items-start gap-2.5">
                  <span className="text-clinical-stable mt-0.5 flex-shrink-0" aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  </span>
                  <span className="text-13.5px text-ink font-sans leading-snug">{beneficio}</span>
                </li>
              ))}
            </ul>

            {/* Con membresía que vender, la comparación va DESPUÉS del bloque
                para activar (auditoría de conversión del 25-oct-2026): en el
                teléfono la tabla dejaba el botón fuera de la hoja. */}
            {!contratable && <ComparaPlanes profile={profile} className="mb-5" />}
          </>
        )}

        {/* Cara sin rol: invita a completar el perfil antes de vender. */}
        {!isNative && cara.invitaPerfil && onCompletarPerfil && (
          <Button
            variant="primary"
            size="md"
            onClick={() => { onCompletarPerfil(); onClose?.(); }}
            className="w-full mb-3"
          >
            Completar mi perfil
          </Button>
        )}

        {isNative ? (
          /* App nativa — consumption-only. El trial es gratuito (sin cobro),
             siempre disponible. La suscripción pagada no se ofrece ni se
             enlaza (Play y App Store, decisión D7). */
          <div className="flex flex-col gap-2.5">
            {error && (
              <p className="text-12px text-system-error font-sans text-center">{error}</p>
            )}
            {!isNative && trialDisponible && (
              <Button variant="primary" size="md" onClick={handleTrial} disabled={trialLoading}>
                {trialLoading ? "Activando…" : ctaLabel}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} className="self-center">
              Seguir con el plan gratis
            </Button>
          </div>
        ) : !contratable ? (
          /* Web, sin membresía que vender aquí (TENS, sin rol o de visita en
             otro mundo): lo mismo que la página de precios, sin precio ni
             compra. De visita, Planes ofrece lo que sí puede contratar. */
          <div className="flex flex-col gap-2.5">
            {error && (
              <p className="text-12px text-system-error font-sans text-center">{error}</p>
            )}
            {!isNative && trialDisponible && (
              <Button variant="primary" size="md" onClick={handleTrial} disabled={trialLoading}>
                {trialLoading ? "Activando…" : ctaLabel}
              </Button>
            )}
            {ofreceEnPlanes && (
              <Button variant="primary" size="md" onClick={onVerPlanes}>
                Ver las membresías
              </Button>
            )}
            <a href="/precios" target="_blank" rel="noopener noreferrer" className="text-12px text-accent font-sans underline self-center">
              Ver precio y condiciones
            </a>
            <Button variant="ghost" size="sm" onClick={onClose} className="self-center">
              Seguir con el plan gratis
            </Button>
          </div>
        ) : (
          /* Web: el Plus de la experiencia y Full, el mismo bloque de Planes;
             la comparación debajo y "Seguir con el plan gratis" al final. */
          <>
            <ContratarMembresia
              profile={profile}
              origen={`paywall_${feature}`}
              onMetric={onMetric}
              tamano="md"
              trialDisponible={trialDisponible}
              onPagoConfirmado={onTrialIniciado}
              trialLoading={trialLoading}
              errorPrueba={error}
            />
            <ComparaPlanes profile={profile} className="mt-5 mb-3" />
            <div className="flex flex-col">
              <Button variant="ghost" size="sm" onClick={onClose} className="self-center">
                Seguir con el plan gratis
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Hoja sobria del límite de uso con el acceso abierto. Exportada para test.
export function HojaLimiteAbierto({ feature, contexto, onClose, onVerPlanes, vuelta = null }) {
  // Un título por límite que existe; lo que no es un límite mensual (el PDF,
  // por ejemplo) dice la frase general, la misma de la hoja nativa.
  const titulo = feature === "detallado"
    ? "Llegaste al límite de PAE Detallado de este mes"
    : feature === "revisa"
      ? "Llegaste al límite de Revisa de este mes"
      : feature === "pae" || feature === "pauta"
        ? "Llegaste al límite de PAE de este mes"
        : "Llegaste al límite del plan gratis.";
  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="limite-title"
    >
      <div
        className="bg-paper w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 shadow-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1 bg-edge rounded-full mx-auto mb-4 sm:hidden" />
        <h2 id="limite-title" className="font-serif text-xl font-bold text-ink mb-2">
          {titulo}
        </h2>
        {contexto && (
          <p className="text-sm text-ink-subtle font-sans leading-relaxed mb-1.5">{contexto}</p>
        )}
        <p className="text-sm text-ink-subtle font-sans leading-relaxed mb-5">
          Lo que ya creaste sigue disponible, y el resto de MiPAE no cambia.
        </p>
        {vuelta && (
          <p className="text-sm text-ink font-sans leading-relaxed mb-5">
            Fuera de tu casa rige el plan gratis. En tu casa, con tu membresía, no tienes este límite.
          </p>
        )}
        <div className="flex flex-col gap-2.5">
          {vuelta && (
            <Button variant="primary" size="md" onClick={() => { vuelta.onVolver?.(); onClose?.(); }}>
              Volver a {vuelta.casa}
            </Button>
          )}
          <Button variant={vuelta ? "secondary" : "primary"} size="md" onClick={onClose}>
            Entendido
          </Button>
          {onVerPlanes && (
            <>
              {/* El PAE es de MiPAE Estudiante: lo amplían las dos membresías
                  que la cubren, no una sola (desde el 17-oct hay tres). */}
              <p className="text-12px text-ink-muted font-sans text-center mt-1">
                Con Estudiante Plus o Full, el PAE queda sin el límite del plan gratis.
              </p>
              <Button variant="ghost" size="sm" onClick={onVerPlanes} className="self-center">
                Ver las membresías
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


